import * as api from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

export type SiteStructureContent =
    | api.SiteSectionGroup
    | api.SiteSection
    | api.SiteSpace
    | api.SiteExternalLink;

/**
 * Filter out site external links from a list of site structure items, narrowing the type to
 * sections and section groups.
 */
export function excludeSiteExternalLinks(
    items: Array<api.SiteSection | api.SiteSectionGroup | api.SiteExternalLink>
): Array<api.SiteSection | api.SiteSectionGroup> {
    return items.filter(
        (item): item is api.SiteSection | api.SiteSectionGroup =>
            item.object !== 'site-external-link'
    );
}

/**
 * Read section-group children while supporting legacy payloads still using `sections`.
 * Returns all children, including site external links; callers switch on `.object` or narrow with
 * `excludeSiteExternalLinks`.
 */
export function getSiteSectionGroupChildren(
    sectionGroup: api.SiteSectionGroup
): (api.SiteSection | api.SiteSectionGroup | api.SiteExternalLink)[] {
    if (sectionGroup.children.length > 0) {
        return sectionGroup.children;
    }

    return sectionGroup.sections;
}

export function flattenGroupedSections(
    structure: (api.SiteSection | api.SiteSectionGroup | api.SiteExternalLink)[]
): api.SiteSection[] {
    return structure.flatMap((item) => {
        switch (item.object) {
            case 'site-section':
                return item;
            case 'site-section-group':
                return getSiteSectionsFromSiteSectionGroup(item);
            case 'site-external-link':
                return [];
            default:
                return assertNever(item);
        }
    });
}

export function flattenGroupedSectionGroups(
    structure: (api.SiteSection | api.SiteSectionGroup | api.SiteExternalLink)[]
): api.SiteSectionGroup[] {
    return structure.flatMap((item) =>
        item.object === 'site-section-group'
            ? [item, ...flattenGroupedSectionGroups(getSiteSectionGroupChildren(item))]
            : []
    );
}

export function flattenSiteExternalLinks(
    structure: (api.SiteSection | api.SiteSectionGroup | api.SiteExternalLink)[]
): api.SiteExternalLink[] {
    return structure.flatMap((item) => {
        switch (item.object) {
            case 'site-external-link':
                return item;
            case 'site-section-group':
                return flattenSiteExternalLinks(getSiteSectionGroupChildren(item));
            case 'site-section':
                return [];
            default:
                return assertNever(item);
        }
    });
}

/**
 * Get the site spaces from the site structure.
 */
export function getSiteSpacesFromStructure(siteStructure: api.SiteStructure): api.SiteSpace[] {
    switch (siteStructure.type) {
        case 'siteSpaces': {
            return siteStructure.structure;
        }
        case 'sections': {
            return siteStructure.structure.flatMap((sectionOrGroup) => {
                switch (sectionOrGroup.object) {
                    case 'site-section':
                        return sectionOrGroup.siteSpaces;
                    case 'site-section-group':
                        return getSiteSpacesFromSiteSectionGroup(sectionOrGroup);
                    case 'site-external-link':
                        return [];
                    default:
                        assertNever(sectionOrGroup);
                }
            });
        }
        default:
            assertNever(siteStructure);
    }
}

/** Get the site spaces that have a custom home page configured. */
export function getSiteSpacesWithCustomHomePage(siteStructure: api.SiteStructure): api.SiteSpace[] {
    return getSiteSpacesFromStructure(siteStructure).filter((siteSpace) =>
        Boolean(siteSpace.pageId)
    );
}

/**
 * Check if the site structure is empty.
 */
export function checkIsSiteStructureEmpty(siteStructure: api.SiteStructure): boolean {
    if (siteStructure.type === 'siteSpaces') {
        return siteStructure.structure.length === 0;
    }
    return false;
}

export function flattenSiteStructure(siteStructure: api.SiteStructure): SiteStructureContent[] {
    const siteSpaces = getSiteSpacesFromStructure(siteStructure);
    if (siteStructure.type === 'sections') {
        return [
            ...flattenGroupedSections(siteStructure.structure),
            ...flattenGroupedSectionGroups(siteStructure.structure),
            ...flattenSiteExternalLinks(siteStructure.structure),
            ...siteSpaces,
        ];
    }
    return siteSpaces;
}

function getIndexInSiteItems(items: SiteStructureContent[], itemId: string): number {
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.id === itemId) {
            return i;
        }
        const childIndex = (() => {
            switch (item.object) {
                case 'site-section-group':
                    return getIndexInSiteItems(getSiteSectionGroupChildren(item), itemId);
                case 'site-section':
                    return getIndexInSiteItems(item.siteSpaces, itemId);
                case 'site-space':
                case 'site-external-link':
                    return -1;
                default:
                    assertNever(item);
            }
        })();
        if (childIndex !== -1) {
            return childIndex;
        }
    }
    return -1;
}

export function getIndexInSiteStructure(siteStructure: api.SiteStructure, itemId: string): number {
    return getIndexInSiteItems(getSiteStructureTopLevelItems(siteStructure), itemId);
}

export function getSiblingsInSiteItems(items: SiteStructureContent[], itemId: string) {
    if (items.find((item) => item.id === itemId)) {
        return items;
    }
    for (const item of items) {
        const childItems = (() => {
            switch (item.object) {
                case 'site-section-group':
                    return getSiblingsInSiteItems(getSiteSectionGroupChildren(item), itemId);
                case 'site-section':
                    return getSiblingsInSiteItems(item.siteSpaces, itemId);
                case 'site-space':
                case 'site-external-link':
                    return null;
                default:
                    assertNever(item);
            }
        })();
        if (childItems) {
            return childItems;
        }
    }
    return null;
}

export function getSiblingsInSiteStructure(siteStructure: api.SiteStructure, itemId: string) {
    return getSiblingsInSiteItems(getSiteStructureTopLevelItems(siteStructure), itemId);
}

/**
 * Get the top-level items of a site structure as `SiteStructureContent`, including root-level
 * site external links so they participate in index/sibling traversal like other top-level items.
 */
function getSiteStructureTopLevelItems(siteStructure: api.SiteStructure): SiteStructureContent[] {
    return siteStructure.structure;
}

/**
 * Recursively get all the sections starting from a section group.
 */
export function getSiteSectionsFromSiteSectionGroup(
    sectionGroup: api.SiteSectionGroup
): api.SiteSection[] {
    return getSiteSectionGroupChildren(sectionGroup).flatMap((child) => {
        switch (child.object) {
            case 'site-section':
                return [child];
            case 'site-section-group':
                return getSiteSectionsFromSiteSectionGroup(child);
            case 'site-external-link':
                return [];
            default:
                return assertNever(child);
        }
    });
}

/**
 * Recursively get all the site spaces starting from a section group.
 */
export function getSiteSpacesFromSiteSectionGroup(
    sectionGroup: api.SiteSectionGroup
): api.SiteSpace[] {
    return getSiteSectionGroupChildren(sectionGroup).flatMap((child) => {
        switch (child.object) {
            case 'site-section':
                return child.siteSpaces;
            case 'site-section-group':
                return getSiteSpacesFromSiteSectionGroup(child);
            case 'site-external-link':
                return [];
            default:
                return assertNever(child);
        }
    });
}
